import { Link } from "react-router-dom";
import { MapPin, Phone, MessageCircle, Clock, Train, Car, Mail, Check, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { CONTACT, SITE_URL } from "@/content";
import { useSettings } from "@/hooks/useSettings";

const expect = [
  "Karten zum Anfassen, Prüfen & direkt Mitnehmen",
  "Persönliche Beratung von Sammlern für Sammler",
  "Pokémon & One Piece als Schwerpunkt – plus Yu-Gi-Oh! & Zubehör",
  "Anime-Figuren & Merch zum Live-Ansehen",
  "Karten-Ankauf mit Sofort-Auszahlung",
  "Trade Days & Community-Atmosphäre",
];

export default function Anfahrt() {
  const { hours } = useSettings();
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Startseite", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Anfahrt", item: `${SITE_URL}/anfahrt` },
    ],
  };

  return (
    <div data-testid="anfahrt-page">
      <Seo
        title="Anfahrt & Öffnungszeiten | Shibuya Anime & TCG Store Köln"
        description="So findest du den Shibuya Anime & TCG Store in Köln: Zülpicher Str. 357, 50935 Köln. Anfahrt mit Bahn (Linien 9 & 13, Haltestelle Zülpicher Str./Gürtel), Parken, Öffnungszeiten & Karte."
        path="/anfahrt"
        keywords="Shibuya Store Köln Anfahrt, Anime Store Köln Adresse, TCG Store Köln Öffnungszeiten, Zülpicher Straße 357 Köln, Card Shop Köln Sülz"
      />
      <JsonLd id="bc-anfahrt" data={breadcrumbSchema} />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="hero-glow absolute inset-0" />
        <div className="bg-grid absolute inset-0" />
        <div aria-hidden="true" className="pointer-events-none absolute -right-6 top-0 select-none font-jp text-[140px] font-black leading-none text-white/[0.05] md:text-[220px]">場所</div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
          <nav data-testid="breadcrumb" className="flex items-center gap-1.5 font-mono-ui text-[11px] uppercase tracking-wider text-muted-foreground">
            <Link to="/" className="transition-colors hover:text-foreground">Start</Link>
            <ChevronRight className="h-3 w-3" /> <span className="text-primary">Anfahrt</span>
          </nav>
          <Reveal>
            <Badge variant="outline" className="mt-6 rounded-full border-primary/40 bg-primary/10 px-4 py-1.5 font-mono-ui text-[11px] uppercase tracking-widest text-primary">
              <MapPin className="mr-1.5 h-3 w-3" /> Köln-Sülz
            </Badge>
            <h1 className="mt-5 max-w-3xl font-display text-4xl uppercase leading-[0.95] tracking-[0.04em] sm:text-5xl lg:text-6xl">
              Anfahrt zum Shibuya Anime &amp; TCG Store in Köln
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Du findest uns mitten in Köln-Sülz auf der Zülpicher Straße. Hier
              erfährst du, wie du uns mit Bahn, Bus oder Auto erreichst – inklusive
              Öffnungszeiten und Karte.
            </p>
          </Reveal>
        </div>
      </section>

      {/* INFO + MAP */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="text-sm font-medium">Adresse</p>
                  <p className="text-sm text-muted-foreground">{CONTACT.companyName}</p>
                  <p className="text-sm text-muted-foreground">{CONTACT.addressStreet}, {CONTACT.addressCity}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div className="w-full">
                  <p className="text-sm font-medium">Öffnungszeiten</p>
                  <div data-testid="anfahrt-hours" className="mt-1.5 space-y-1">
                    {hours.map((row) => (
                      <div key={row.days} className="flex justify-between gap-4 text-sm text-muted-foreground">
                        <span>{row.days}</span>
                        <span className={row.time === "Geschlossen" ? "" : "text-foreground"}>{row.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                <Train className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="text-sm font-medium">Mit Bus &amp; Bahn (ÖPNV)</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    Nächste Haltestelle: <span className="text-foreground">Zülpicher Str./Gürtel</span> (KVB-Stadtbahn
                    {" "}<span className="text-foreground">Linien 9 &amp; 13</span>), nur wenige Gehminuten entfernt.
                    Von dort die Zülpicher Straße entlang bis zur Hausnummer 357.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                <Car className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="text-sm font-medium">Mit dem Auto / Parken</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    In den Seitenstraßen rund um die Zülpicher Straße in Sülz findest du
                    Parkplätze am Straßenrand (teils bewirtschaftet). Auch rund um die
                    Haltestelle Zülpicher Str./Gürtel gibt es Parkmöglichkeiten.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 pt-2">
                <Button asChild className="h-11 rounded-xl bg-primary px-5 font-medium text-primary-foreground">
                  <a href={CONTACT.phoneHref}><Phone className="mr-2 h-4 w-4" /> {CONTACT.phoneDisplay}</a>
                </Button>
                <Button asChild variant="outline" className="h-11 rounded-xl border-primary/30 bg-transparent px-5 hover:bg-primary/10">
                  <a href={CONTACT.mapsHref} target="_blank" rel="noopener noreferrer"><MapPin className="mr-2 h-4 w-4" /> Route in Google Maps</a>
                </Button>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <div className="h-full min-h-[360px] overflow-hidden rounded-2xl border border-border shadow-xl shadow-black/40">
              <iframe
                title="Shibuya Store Köln – Anfahrt & Standort"
                data-testid="anfahrt-map-embed"
                src={CONTACT.mapsEmbed}
                className="h-full min-h-[360px] w-full"
                style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) saturate(0.5)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WAS DICH ERWARTET + CTA */}
      <section className="border-t border-border bg-card/50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <h2 className="font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
              Das erwartet dich <span className="neon-text">im Store.</span>
            </h2>
          </Reveal>
          <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {expect.map((item, i) => (
              <Reveal key={item} delay={i * 0.05}>
                <li className="flex items-start gap-2.5 rounded-xl border border-border bg-card p-4 text-sm">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15">
                    <Check className="h-3 w-3 text-primary" />
                  </span>
                  <span className="text-muted-foreground">{item}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg" className="h-12 rounded-xl bg-primary px-6 font-medium text-primary-foreground">
              <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer"><MessageCircle className="mr-2 h-5 w-5" /> Besuche uns – per WhatsApp melden</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-12 rounded-xl border-primary/30 bg-transparent px-6 hover:bg-primary/10">
              <a href={CONTACT.emailHref}><Mail className="mr-2 h-5 w-5" /> {CONTACT.email}</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
